import { Tag } from '../types';
import { TAGS_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const useTagsApi = () => {
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getTags = async (): Promise<Tag[]> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${TAGS_URL}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new ApiError(
          `Failed to fetch tags: ${errorText}`,
          response.status
        );
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching tags:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error('Failed to fetch tags');
    }
  };

  const createTag = async (tagName: string): Promise<Tag> => {
    if (!tagName.trim()) {
      throw new Error('Tag name cannot be empty');
    }

    try {
      const headers = await getHeaders();
      const response = await fetch(`${TAGS_URL}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ tagName })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new ApiError(
          `Failed to create tag: ${errorText}`,
          response.status
        );
      }

      return response.json();
    } catch (error) {
      console.error('Error creating tag:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error('Failed to create tag');
    }
  };

  // This function will either find an existing tag or create a new one
  const findOrCreateTag = async (tagName: string): Promise<Tag> => {
    try {
      // First, get all tags to check if it exists
      const tags = await getTags();
      const existingTag = tags.find(
        tag => tag.tagName.toLowerCase() === tagName.toLowerCase()
      );
      
      if (existingTag) {
        return existingTag;
      }
      
      // If not found, create a new tag
      return await createTag(tagName);
    } catch (error) {
      console.error('Error in findOrCreateTag:', error);
      throw error;
    }
  };

  // Create multiple tags at once from an array of tag names
  const createMultipleTags = async (tagNames: string[]): Promise<Tag[]> => {
    // Create a unique list of non-empty tag names
    const uniqueTagNames: string[] = [];
    tagNames.forEach(name => {
      if (name.trim() && !uniqueTagNames.includes(name.trim())) {
        uniqueTagNames.push(name.trim());
      }
    });
    
    const createdTags: Tag[] = [];
    for (const tagName of uniqueTagNames) {
      try {
        const tag = await findOrCreateTag(tagName);
        createdTags.push(tag);
      } catch (error) {
        console.error(`Error creating tag "${tagName}":`, error);
      }
    }
    
    return createdTags;
  };

  return {
    getTags,
    createTag,
    findOrCreateTag,
    createMultipleTags
  };
}